// Floor sale, step 3: the bag. Lines, customer, totals, then on to payment.
import React from "react";
import { formatMoney } from "../../../../utils/fashion/money";
import { taxIncluded } from "../../../../utils/fashion/billTotals";
import { lineTitle } from "../../../../utils/fashion/lineQty";
import { bagLines, billTotals } from "./floorHelpers";
import { Back, FloorImage, Header } from "./FloorParts";
import FloorCustomer from "./FloorCustomer";

export default function FloorBag({ cart, tillData, count, productById, artFor, onBack, onPay, increaseProductQty, decreaseProductQty, deleteProduct, customerProps }) {
  const lines = bagLines(cart, productById);
  const totals = billTotals(cart);
  const money = (v) => formatMoney(v, tillData);
  const gstIncluded = taxIncluded(tillData);
  // Display only: what the pieces cost before offers (and before GST when it is added on top).
  const subtotal = totals.total + totals.discount - (gstIncluded ? 0 : totals.tax);

  return (
    <>
      <Header>
        <Back onClick={onBack}>Keep shopping</Back>
        <b className="fp-fl-mk">
          Bag · <span className="fp-num">{count}</span>
        </b>
      </Header>
      <div className="fp-fl-body">
        {lines.length === 0 ? (
          <div className="fp-fl-empty">
            <b>The bag is empty</b>
            <span>Pick a style to add it.</span>
          </div>
        ) : (
          <ul className="fp-fl-lines" aria-label="Items in the bag">
            {lines.map(({ item, variant }) => {
              const qty = Number(item.weight) || 0;
              const name = item.name || "Item";
              return (
                <li key={item.lineId || item.productId} className="fp-fl-line">
                  <FloorImage src={item.imageurl || productById?.(item.productId)?.imageurl} alt={name} className="fp-fl-th" art={artFor?.(item.productId)?.art} colour={artFor?.(item.productId)?.colour} />
                  <div className="fp-fl-grow">
                    <b title={name}>{lineTitle(name, variant)}</b>
                    {variant && <small className="fp-fl-mute">{variant}</small>}
                    <div className="fp-fl-row fp-fl-nowrap">
                      <div className="fp-fl-step" role="group" aria-label={`Quantity of ${name}`}>
                        <button type="button" aria-label={`One less ${name}`} disabled={!decreaseProductQty || qty <= 1} onClick={() => decreaseProductQty(item)}>
                          −
                        </button>
                        <span className="fp-num" aria-live="polite">
                          {qty}
                        </span>
                        <button type="button" aria-label={`One more ${name}`} disabled={!increaseProductQty} onClick={() => increaseProductQty(item)}>
                          +
                        </button>
                      </div>
                      {deleteProduct && (
                        <button type="button" className="fp-fl-link" onClick={() => deleteProduct(item)}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  <b className="fp-num">{money(item.nettotal)}</b>
                </li>
              );
            })}
          </ul>
        )}
        <FloorCustomer {...customerProps} />
        {lines.length > 0 && (
          <dl className="fp-fl-tot">
            <div>
              <dt>Subtotal</dt>
              <dd className="fp-num">{money(subtotal)}</dd>
            </div>
            {totals.discount > 0 && (
              <div>
                <dt>Offer savings</dt>
                <dd className="fp-num fp-fl-save">{money(-totals.discount)}</dd>
              </div>
            )}
            <div className={gstIncluded ? "fp-fl-mute" : undefined}>
              <dt>{gstIncluded ? "GST included" : "GST"}</dt>
              <dd className="fp-num">{money(totals.tax)}</dd>
            </div>
          </dl>
        )}
      </div>
      <footer className="fp-fl-ft">
        <div className="fp-fl-paysum">
          <span>To pay</span>
          <b className="fp-num">{money(totals.total)}</b>
        </div>
        <button type="button" className="fp-fl-btn fp-fl-pri fp-fl-wide" disabled={lines.length === 0} onClick={onPay}>
          Take payment
        </button>
      </footer>
    </>
  );
}
